import { useRef, useCallback } from 'react';

/**
 * Hook to manage friend location markers interactions
 * Handles:
 * - Pan to marker on selection
 * - Z-index management for visibility
 * - Info window display
 * - Geocoding for location names
 */
export const useFriendLocationMarkers = (mapRef) => {
  const markersRef = useRef({});
  const previousSelectedRef = useRef(null);

  /**
   * Pan map to friend marker and bring it to front
   */
  const panToFriendMarker = useCallback(
    (friend) => {
      if (!mapRef?.current) {
        console.warn('Map reference not available');
        return;
      }

      const { latitude, longitude } = friend.location;

      // Validate coordinates
      if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
        console.warn('Invalid friend location coordinates');
        return;
      }

      try {
        // Pan to the friend location
        mapRef.current.panTo({
          lat: parseFloat(latitude),
          lng: parseFloat(longitude),
        });

        // Zoom to a suitable level for marker visibility
        mapRef.current.setZoom(16);

        // Update marker z-index
        updateMarkerZIndex(friend._id);
      } catch (error) {
        console.error('Error panning to friend marker:', error);
      }
    },
    [mapRef]
  );

  /**
   * Update z-index of markers - bring selected to front
   */
  const updateMarkerZIndex = useCallback((selectedFriendId) => {
    // Reset previous marker's z-index
    if (previousSelectedRef.current && markersRef.current[previousSelectedRef.current]) {
      const previousMarker = markersRef.current[previousSelectedRef.current];
      if (previousMarker.markerElement) {
        previousMarker.markerElement.style.zIndex = '10';
      }
    }

    // Bring selected marker to front
    if (markersRef.current[selectedFriendId]) {
      const currentMarker = markersRef.current[selectedFriendId];
      if (currentMarker.markerElement) {
        currentMarker.markerElement.style.zIndex = '100';
        currentMarker.markerElement.style.transform = 'scale(1.15)';
        currentMarker.markerElement.style.transition = 'all 0.3s ease';
      }
    }

    previousSelectedRef.current = selectedFriendId;
  }, []);

  /**
   * Register a friend marker element for z-index management
   */
  const registerMarkerElement = useCallback((friendId, markerElement) => {
    if (!markersRef.current[friendId]) {
      markersRef.current[friendId] = {};
    }
    markersRef.current[friendId].markerElement = markerElement;
  }, []);

  /**
   * Get location name using reverse geocoding
   */
  const getLocationName = useCallback(async (latitude, longitude) => {
    try {
      if (!window.google?.maps?.Geocoder) {
        console.warn('Google Maps Geocoder not available');
        return 'Location loaded';
      }

      return new Promise((resolve) => {
        const geocoder = new window.google.maps.Geocoder();
        const latlng = {
          lat: parseFloat(latitude),
          lng: parseFloat(longitude),
        };

        const timeoutId = setTimeout(() => {
          resolve('Location loaded');
        }, 5000); // 5 second timeout

        geocoder.geocode({ location: latlng }, (results, status) => {
          clearTimeout(timeoutId);

          if (status === 'OK' && results?.[0]) {
            // Try to get the most specific address component
            const addressComponents = results[0].address_components;
            const locality =
              addressComponents?.find((comp) => comp.types.includes('locality'))?.long_name ||
              addressComponents?.find((comp) => comp.types.includes('administrative_area_level_1'))
                ?.long_name ||
              'Location';

            resolve(locality);
          } else {
            resolve('Location loaded');
          }
        });
      });
    } catch (error) {
      console.error('Error in getLocationName:', error);
      return 'Location loaded';
    }
  }, []);

  /**
   * Reset all markers to default state
   */
  const resetAllMarkers = useCallback(() => {
    Object.values(markersRef.current).forEach((marker) => {
      if (marker.markerElement) {
        marker.markerElement.style.zIndex = '10';
        marker.markerElement.style.transform = 'scale(1)';
      }
    });
    previousSelectedRef.current = null;
  }, []);

  /**
   * Clean up marker references
   */
  const cleanupMarker = useCallback((friendId) => {
    delete markersRef.current[friendId];
  }, []);

  /**
   * Clean up all markers on unmount
   */
  const cleanupAllMarkers = useCallback(() => {
    markersRef.current = {};
    previousSelectedRef.current = null;
  }, []);

  return {
    panToFriendMarker,
    updateMarkerZIndex,
    registerMarkerElement,
    getLocationName,
    resetAllMarkers,
    cleanupMarker,
    cleanupAllMarkers,
  };
};

export default useFriendLocationMarkers;
